import React from 'react';
import {Box, CircularProgress, Stack, Typography} from '@mui/material';
import {InlineNode, DataItemLinkContext, WikidataSearchProvider} from './SourcesInlineNodes';
import SourcesFileProcessingProgress from './SourcesFileProcessingProgress';

// Renders a paragraph's inline nodes, wrapping the char range [start, end) in a highlight <mark>.
// Each transcript paragraph is a single flat PM block, so offsets index its concatenated text.
// Prefer locating matchText (robust against offset drift); fall back to the raw offsets.
function renderInlineWithRange(nodes, start, end, matchText) {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;
    let s = start;
    let e = end;
    if (typeof matchText === 'string' && matchText.length > 0) {
        const full = nodes.map((n) => (typeof n?.text === 'string' ? n.text : '')).join('');
        let idx = full.indexOf(matchText, Math.max(0, (start || 0) - 5));
        if (idx < 0) idx = full.indexOf(matchText);
        if (idx >= 0) { s = idx; e = idx + matchText.length; }
    }
    const out = [];
    let pos = 0;
    nodes.forEach((node, i) => {
        const text = typeof node?.text === 'string' ? node.text : null;
        if (text == null) { out.push(<InlineNode key={i} node={node}/>); return; }
        const nodeStart = pos;
        const nodeEnd = pos + text.length;
        pos = nodeEnd;
        if (nodeEnd <= s || nodeStart >= e) { out.push(<InlineNode key={i} node={node}/>); return; }
        const a = Math.max(s - nodeStart, 0);
        const b = Math.min(e - nodeStart, text.length);
        if (a > 0) out.push(<InlineNode key={`${i}-b`} node={{...node, text: text.slice(0, a)}}/>);
        if (b > a) {
            out.push(
                    <mark
                        key={`${i}-m`}
                        style={{
                            backgroundColor: 'rgba(255, 200, 60, 0.45)',
                            borderRadius: 2,
                            padding: '0 1px',
                            color: 'inherit',
                        }}
                    >
                    <InlineNode node={{...node, text: text.slice(a, b)}}/>
                </mark>,
            );
        }
        if (b < text.length) out.push(<InlineNode key={`${i}-a`} node={{...node, text: text.slice(b)}}/>);
    });
    return out;
}

/** Formats milliseconds → m:ss  (e.g. 0 → "0:00", 90500 → "1:30") */
function formatMs(ms) {
    const total = Math.floor((ms || 0) / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Scrollable transcript panel with per-line timestamps.
 *
 * Layout per line:
 *   ┌───────┬──────────────────────────────────┐
 *   │ 0:00  │  sentence text with entity marks │
 *   │ 0:28  │  …                               │
 *   └───────┴──────────────────────────────────┘
 *
 * Clicking a line seeks the player to that line's startMs and auto-plays.
 * The active (currently playing) line is highlighted.
 * Clicking an entity opens a WikidataEntityDialog popover (does NOT seek).
 */
const SourcesTranscriptView = ({
    paragraphs,
    activeParagraphIndex,
    onSentenceClick,
    isLoading,
    error,
    processingStatus,
    dir = 'ltr',
    onBlockHighlight = null,
    highlightedSegmentIds = null,
    highlightedRanges = null,
    linkContext = null,
    searchMatches = null,
    activeSearchMatchIdx = -1,
}) => {
    const activeRef = React.useRef(null);
    const highlightRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const [selBtn, setSelBtn] = React.useState(null); // {top, left, blockIndex, startOffset, endOffset, text} | null
    const canHighlight = typeof onBlockHighlight === 'function';

    // Track a text selection inside a transcript paragraph and surface a floating highlight button
    // anchored to it. We capture the selection's char offsets WITHIN the paragraph's text so the
    // saved highlight marks just the selected sentence(s), not the whole line.
    React.useEffect(() => {
        if (!canHighlight) return undefined;
        const container = containerRef.current;
        if (!container) return undefined;

        const resolveParaEl = (node) => {
            let el = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
            while (el && el !== container) {
                if (el.dataset && el.dataset.blockIndex != null) return el;
                el = el.parentElement;
            }
            return null;
        };

        const offsetWithin = (paraEl, node, nodeOffset, fallbackToEnd) => {
            if (!node || !paraEl.contains(node)) {
                return fallbackToEnd ? paraEl.textContent.length : 0;
            }
            const r = document.createRange();
            r.selectNodeContents(paraEl);
            r.setEnd(node, nodeOffset);
            return r.toString().length;
        };

        const update = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed || sel.rangeCount === 0) { setSelBtn(null); return; }
            const range = sel.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            if (!rect || (rect.width === 0 && rect.height === 0)) { setSelBtn(null); return; }
            // Anchor to the paragraph where the selection starts; offsets are clamped to it.
            const paraEl = resolveParaEl(range.startContainer) || resolveParaEl(sel.focusNode);
            if (!paraEl || !container.contains(paraEl)) { setSelBtn(null); return; }
            const blockIndex = Number(paraEl.dataset.blockIndex);
            if (!Number.isInteger(blockIndex)) { setSelBtn(null); return; }
            const paraLen = paraEl.textContent.length;
            let startOffset = offsetWithin(paraEl, range.startContainer, range.startOffset, false);
            let endOffset = offsetWithin(paraEl, range.endContainer, range.endOffset, true);
            if (endOffset < startOffset) { const t = startOffset; startOffset = endOffset; endOffset = t; }
            startOffset = Math.max(0, Math.min(startOffset, paraLen));
            endOffset = Math.max(0, Math.min(endOffset, paraLen));
            if (endOffset <= startOffset) { setSelBtn(null); return; }
            const text = paraEl.textContent.slice(startOffset, endOffset);
            const cRect = container.getBoundingClientRect();
            const top = rect.top - cRect.top + container.scrollTop - 34;
            const left = Math.max(8, Math.min(
                rect.left - cRect.left + rect.width / 2 - 12,
                container.clientWidth - 32,
            ));
            setSelBtn({top, left, blockIndex, startOffset, endOffset, text});
        };

        const onSelectionChange = () => update();
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    }, [canHighlight, paragraphs]);

    // Saved-highlight set keyed on the canonical block index (para.blockIndex), matching the
    // numberInItem the highlights API returns.
    const highlightSet = React.useMemo(() => {
        if (!Array.isArray(highlightedSegmentIds) || highlightedSegmentIds.length === 0) return null;
        return new Set(highlightedSegmentIds.map(Number));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedSegmentIds?.join(',')]);

    // Build per-paragraph search match index: blockIndex → [{start, end, globalMatchIdx}].
    const searchMatchesByBlock = React.useMemo(() => {
        if (!Array.isArray(searchMatches) || searchMatches.length === 0) return null;
        const map = {};
        searchMatches.forEach((m, globalMatchIdx) => {
            if (!map[m.blockIndex]) map[m.blockIndex] = [];
            map[m.blockIndex].push({...m, globalMatchIdx});
        });
        return map;
    }, [searchMatches]);

    // The global match index of the active match, so we can render its <mark> with the bright ring.
    const activeGlobalMatchSet = React.useMemo(() => {
        if (activeSearchMatchIdx == null || activeSearchMatchIdx < 0) return null;
        return new Set([activeSearchMatchIdx]);
    }, [activeSearchMatchIdx]);

    // Ref to the active search match <mark> element for scroll-into-view.
    const activeSearchRef = React.useRef(null);

    React.useEffect(() => {
        if (activeSearchRef.current) {
            activeSearchRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [activeSearchMatchIdx]);

    React.useEffect(() => {
        activeRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    }, [activeParagraphIndex]);

    // Scroll to the highlighted paragraph when the active highlight changes.
    React.useEffect(() => {
        if (highlightRef.current) {
            highlightRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedSegmentIds?.join(',')]);

    if (processingStatus?.kind === 'processing') {
        return (
            <SourcesFileProcessingProgress
                status={processingStatus}
                title="Building transcript…"
                compact
            />
        );
    }

    if (isLoading) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" spacing={1.5} p={3}>
                <CircularProgress size={28}/>
                <Typography variant="body2" color="text.secondary">Loading transcript…</Typography>
            </Stack>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Typography variant="caption" color="error">{error}</Typography>
            </Box>
        );
    }

    if (!paragraphs?.length) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" p={3}>
                <Typography variant="body2" color="text.secondary">No transcript available.</Typography>
            </Stack>
        );
    }

    return (
        <DataItemLinkContext.Provider value={linkContext}>
        <WikidataSearchProvider>
        <Box ref={containerRef} sx={{flex: 1, overflowY: 'auto', py: 0.5, position: 'relative'}}>
            {paragraphs.map((para, idx) => {
                const isActive = idx === activeParagraphIndex;
                const blockIndex = Number.isInteger(para.blockIndex) ? para.blockIndex : idx;
                const isSavedHighlight = highlightSet != null && highlightSet.has(blockIndex);
                const range = highlightedRanges && typeof highlightedRanges === 'object'
                    ? highlightedRanges[blockIndex]
                    : null;
                const hasRange = isSavedHighlight && range
                    && Number.isInteger(range.startOffset)
                    && Number.isInteger(range.endOffset)
                    && range.endOffset > range.startOffset;
                const isFirstHighlight = isSavedHighlight
                    && Math.min(...Array.from(highlightSet)) === blockIndex;

                // Search matches for this paragraph.
                const paraSearchMatches = searchMatchesByBlock ? (searchMatchesByBlock[blockIndex] || null) : null;

                // Render paragraph text with optional search match highlights.
                const renderParaText = () => {
                    if (hasRange && para.inlineNodes && para.inlineNodes.length > 0) {
                        return renderInlineWithRange(para.inlineNodes, range.startOffset, range.endOffset, range.text);
                    }
                    if (para.inlineNodes && para.inlineNodes.length > 0) {
                        if (paraSearchMatches && paraSearchMatches.length > 0) {
                            // Render inline nodes with search match highlights.
                            const fullText = para.inlineNodes.map((n) => (typeof n?.text === 'string' ? n.text : '')).join('');
                            const sorted = [...paraSearchMatches].sort((a, b) => a.start - b.start);
                            const parts = [];
                            let cur = 0;
                            sorted.forEach((m, mi) => {
                                if (m.start > cur) {
                                    const sliceText = fullText.slice(cur, m.start);
                                    if (sliceText) parts.push(<React.Fragment key={`t-${mi}`}>{sliceText}</React.Fragment>);
                                }
                                const matchText = fullText.slice(m.start, m.end);
                                const isActiveMatch = activeGlobalMatchSet && activeGlobalMatchSet.has(m.globalMatchIdx);
                                parts.push(
                                    <mark
                                        key={`m-${mi}`}
                                        ref={isActiveMatch ? activeSearchRef : null}
                                        data-search-match={m.globalMatchIdx}
                                        style={{
                                            backgroundColor: isActiveMatch
                                                ? 'rgba(34, 197, 94, 0.55)'
                                                : 'rgba(134, 239, 172, 0.45)',
                                            outline: isActiveMatch ? '2px solid rgba(22, 163, 74, 0.9)' : 'none',
                                            outlineOffset: '1px',
                                            borderRadius: 2,
                                            padding: '0 1px',
                                            color: 'inherit',
                                            fontWeight: isActiveMatch ? 700 : 'inherit',
                                        }}
                                    >
                                        {matchText}
                                    </mark>,
                                );
                                cur = m.end;
                            });
                            if (cur < fullText.length) {
                                parts.push(<React.Fragment key="t-tail">{fullText.slice(cur)}</React.Fragment>);
                            }
                            return parts;
                        }
                        return para.inlineNodes.map((node, ni) => (
                            <InlineNode key={ni} node={node}/>
                        ));
                    }
                    return para.text;
                };

                return (
                    <Box
                        key={idx}
                        data-block-index={blockIndex}
                        ref={isFirstHighlight ? highlightRef : (isActive ? activeRef : null)}
                        onClick={() => onSentenceClick?.(para.startMs)}
                        sx={(theme) => ({
                            position: 'relative',
                            display: 'flex',
                            flexDirection: dir === 'rtl' ? 'row-reverse' : 'row',
                            alignItems: 'flex-start',
                            gap: 1,
                            py: 0.75,
                            px: 1.5,
                            mx: 0.5,
                            mb: 0.25,
                            borderRadius: 1,
                            cursor: 'pointer',
                            // When a sub-range mark is active, don't shade the whole line — the inline
                            // <mark> is the cue; keep only the active-playback shade.
                            bgcolor: isActive
                                ? 'rgba(255, 236, 60, 0.45)'
                                : (isSavedHighlight && !hasRange)
                                    ? 'rgba(255, 200, 60, 0.28)'
                                    : 'transparent',
                            borderLeft: isSavedHighlight ? '3px solid rgba(255,165,0,0.9)' : '3px solid transparent',
                            color: theme.palette.text.primary,
                            transition: theme.transitions.create(['background-color']),
                            '&:hover': {
                                bgcolor: isActive
                                    ? 'rgba(255, 236, 60, 0.65)'
                                    : isSavedHighlight
                                        ? 'rgba(255, 200, 60, 0.4)'
                                        : theme.palette.action.hover,
                            },
                        })}
                    >
                        {/* Timestamp — fixed-width, monospaced */}
                        <Typography
                            variant="caption"
                            component="span"
                            sx={(theme) => ({
                                minWidth: 36,
                                flexShrink: 0,
                                fontVariantNumeric: 'tabular-nums',
                                fontWeight: isActive ? 700 : 500,
                                lineHeight: 1.6,
                                mt: '1px',
                                color: isActive
                                    ? theme.palette.primary.main
                                    : theme.palette.primary.main,
                                opacity: isActive ? 1 : 0.85,
                            })}
                        >
                            {formatMs(para.startMs)}
                        </Typography>

                        {/* Sentence text — entities are blue dotted-underline with type badges */}
                        <Typography
                            variant="body2"
                            component="span"
                            dir={dir}
                            sx={{lineHeight: 1.6, flex: 1, textAlign: dir === 'rtl' ? 'right' : 'left'}}
                        >
                            {renderParaText()}
                        </Typography>

                    </Box>
                );
            })}

            {/* Floating highlight button anchored to the current text selection. */}
            {canHighlight && selBtn && (
                <button
                    type="button"
                    title="Add highlight"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onBlockHighlight({
                            blockIndex: selBtn.blockIndex,
                            startOffset: selBtn.startOffset,
                            endOffset: selBtn.endOffset,
                            text: selBtn.text,
                        });
                        setSelBtn(null);
                        window.getSelection()?.removeAllRanges();
                    }}
                    style={{
                        position: 'absolute',
                        top: selBtn.top,
                        left: selBtn.left,
                        width: 24,
                        height: 24,
                        border: 'none',
                        borderRadius: 4,
                        background: 'rgba(255,165,0,0.95)',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        lineHeight: 1,
                        padding: 0,
                        zIndex: 20,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                    }}
                >
                    ★
                </button>
            )}
        </Box>
        </WikidataSearchProvider>
        </DataItemLinkContext.Provider>
    );
};

export default SourcesTranscriptView;
